import React, { useRef, useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import {
  ArrowLeft,
  ArrowRight,
  ArrowsContainerMobile,
  Card,
  CardTextBlock,
  CardsContainer,
  MeetMightyProSliderContainer,
} from './MeetMightyProSlider.styles';
import { IStaticContentfulImages } from 'src/types/contentful';
import Heading from 'src/elements/typography/heading';
import Paragraph from 'src/elements/typography/paragraph';
import { palette } from 'src/styles/colors';
import Image from 'src/elements/image/image';
import { IGatsbyImageData } from 'gatsby-plugin-image';

const CARDS_LIST = [
  {
    id: 'cardResult7',
    title: 'Scaling high-ticket\n courses with 99%\n margins',
    subtitle:
      'We turned content this entrepreneur had into a $12,000 high-ticket membership that sold out immediately.',
  },
  {
    id: 'cardResult6',
    title: 'A single place to\n build a $1M+ business',
    subtitle:
      'We helped a celebrity entrepreneur move off another platform with high churn to build a thriving multi-million dollar business on Mighty.',
  },
  {
    id: 'cardResult5',
    title: 'Multiple $500K\n launches in one year',
    subtitle:
      'We helped a life coach struggling to sell courses reimagine his programs on Mighty Pro. The result was 3 6-figure launches and 96% engagement.',
  },
  {
    id: 'cardResult4',
    title: 'A 10x ROI in just a\n few days',
    subtitle:
      'We helped a group of YouTube creators design a $398K launch on Mighty Pro after losing 80% of members to payment errors on another platform.',
  },
  {
    id: 'cardResult3',
    title: '$3M jump in revenue\n by streamlining apps',
    subtitle:
      'We streamlined a fitness influencer’s business from 3 disjointed apps to a single branded app experience.',
  },
  {
    id: 'cardResult2',
    title: '100% revenue\n growth in a year',
    subtitle: 'We helped a YouTube coach grow revenue from $2.8M to $7.6M in less than a year by increasing retention.',
  },
  {
    id: 'cardResult1',
    title: '$5M course launch\n in 10 days',
    subtitle:
      'We helped a podcaster migrate to Mighty, where 5,000 people signed up for their $1,000 program in just 10 days.',
  },
];

interface CardItemProps {
  card: (typeof CARDS_LIST)[0];
  image: IGatsbyImageData;
  className?: string;
}

const CardItem = ({ card, image, className }: CardItemProps): JSX.Element => (
  <Card className={className}>
    <Image
      childImageSharp={image}
      alt=''
    />
    <CardTextBlock>
      <Heading
        text={card.title}
        align='center'
        weight='normal'
        standardSize='1.25'
        sizeXxs='1rem'
        color={palette.white}
      />
      <Paragraph
        text={card.subtitle}
        align='center'
        weight='light'
        standardSize='1'
        sizeSm='1.5rem'
        color={palette.white}
      />
    </CardTextBlock>
  </Card>
);

interface MeetMightyProSliderProps {
  fullHeight?: boolean;
}

const MeetMightyProSlider = ({ fullHeight = true }: MeetMightyProSliderProps): JSX.Element => {
  const imagesList = useStaticQuery<IStaticContentfulImages>(graphql`
    {
      cardResult1: contentfulAsset(contentful_id: { eq: "71qYGAhlEaYVmc9H459oqB" }) {
        gatsbyImageData(width: 640, layout: CONSTRAINED, placeholder: BLURRED)
      }
      cardResult2: contentfulAsset(contentful_id: { eq: "6qLORaWCxocMpfX6Le6k4d" }) {
        gatsbyImageData(width: 640, layout: CONSTRAINED, placeholder: BLURRED)
      }
      cardResult3: contentfulAsset(contentful_id: { eq: "3FZcal4K57beIecuW5Ro8P" }) {
        gatsbyImageData(width: 640, layout: CONSTRAINED, placeholder: BLURRED)
      }
      cardResult4: contentfulAsset(contentful_id: { eq: "6zwhmnrP65Jyfe9mn07gRe" }) {
        gatsbyImageData(width: 640, layout: CONSTRAINED, placeholder: BLURRED)
      }
      cardResult5: contentfulAsset(contentful_id: { eq: "1Zo8oDtbcciizDi7C3ruq2" }) {
        gatsbyImageData(width: 640, layout: CONSTRAINED, placeholder: BLURRED)
      }
      cardResult6: contentfulAsset(contentful_id: { eq: "3l8usjLFfVnlYd8iaAttvq" }) {
        gatsbyImageData(width: 640, layout: CONSTRAINED, placeholder: BLURRED)
      }
      cardResult7: contentfulAsset(contentful_id: { eq: "QDuW4UkVn80T7M3rEHu3l" }) {
        gatsbyImageData(width: 640, layout: CONSTRAINED, placeholder: BLURRED)
      }
      arrowLeft: contentfulAsset(contentful_id: { eq: "3YrM02bVaa4FqZJrpksI7P" }) {
        gatsbyImageData(width: 150, layout: CONSTRAINED, placeholder: BLURRED)
      }
      arrowRight: contentfulAsset(contentful_id: { eq: "3GtY6puBODRfNyNHODyUia" }) {
        gatsbyImageData(width: 150, layout: CONSTRAINED, placeholder: BLURRED)
      }
    }
  `);

  const cardsContainerRef = useRef<HTMLDivElement>();
  const [currentCardIndex, setCurrentCardIndex] = useState(CARDS_LIST.length - 1);
  const [isReturning, setIsReturning] = useState(false);

  const secondCardIndex = currentCardIndex === 0 ? CARDS_LIST.length - 1 : currentCardIndex - 1;
  const thirdCardIndex = secondCardIndex === 0 ? CARDS_LIST.length - 1 : secondCardIndex - 1;
  const fourthCardIndex = thirdCardIndex === 0 ? CARDS_LIST.length - 1 : thirdCardIndex - 1;
  const previousCardIndex = currentCardIndex === CARDS_LIST.length - 1 ? 0 : currentCardIndex + 1;

  const onClickRightArrow = () => {
    setIsReturning(false);
    setCurrentCardIndex((prev) => {
      return prev === 0 ? CARDS_LIST.length - 1 : prev - 1;
    });
  };

  const onClickLeftArrow = () => {
    setIsReturning(true);
    setCurrentCardIndex((prev) => {
      return prev === CARDS_LIST.length - 1 ? 0 : prev + 1;
    });
  };

  const cardsToRender = [
    { card: CARDS_LIST[fourthCardIndex], index: fourthCardIndex },
    { card: CARDS_LIST[thirdCardIndex], index: thirdCardIndex },
    { card: CARDS_LIST[secondCardIndex], index: secondCardIndex },
    { card: CARDS_LIST[currentCardIndex], index: currentCardIndex },
    { card: CARDS_LIST[previousCardIndex], index: previousCardIndex },
  ];

  const getAnimationClassForward = (index: number) => {
    switch (index) {
      case thirdCardIndex:
        return 'third-card-appear';
      case secondCardIndex:
        return 'from-right-to-left';
      case currentCardIndex:
        return 'from-left-to-center';
      case previousCardIndex:
        return 'from-center-to-bottom';
      case fourthCardIndex:
        return 'hidden';
      default:
        return null;
    }
  };

  const getReverseAnimationClass = (index: number) => {
    switch (index) {
      case previousCardIndex:
        return 'hidden';
      case currentCardIndex:
        return 'from-bottom-to-center';
      case secondCardIndex:
        return 'from-center-to-left';
      case thirdCardIndex:
        return 'from-left-to-right';
      case fourthCardIndex:
        return 'hidden';
    }
  };

  const getAnimationClass = (index: number) =>
    isReturning ? getReverseAnimationClass(index) : getAnimationClassForward(index);

  return (
    <MeetMightyProSliderContainer>
      <ArrowLeft
        onClick={onClickLeftArrow}
        isMobileOnly
      >
        <Image
          childImageSharp={imagesList.arrowLeft.gatsbyImageData}
          alt=''
        />
      </ArrowLeft>
      <ArrowRight
        onClick={onClickRightArrow}
        isMobileOnly
      >
        <Image
          childImageSharp={imagesList.arrowRight.gatsbyImageData}
          alt=''
        />
      </ArrowRight>
      <CardsContainer
        ref={cardsContainerRef}
        fullHeight={fullHeight}
      >
        {cardsToRender.map(({ card, index }) => (
          <CardItem
            card={card}
            image={imagesList[card.id].gatsbyImageData}
            key={card.id}
            className={getAnimationClass(index)}
          />
        ))}
      </CardsContainer>
      <ArrowsContainerMobile>
        <ArrowLeft onClick={onClickLeftArrow}>
          <Image
            childImageSharp={imagesList.arrowLeft.gatsbyImageData}
            alt=''
          />
        </ArrowLeft>
        <ArrowRight onClick={onClickRightArrow}>
          <Image
            childImageSharp={imagesList.arrowRight.gatsbyImageData}
            alt=''
          />
        </ArrowRight>
      </ArrowsContainerMobile>
    </MeetMightyProSliderContainer>
  );
};

export default MeetMightyProSlider;
